<script lang="ts">
import { defineBlock } from '@bagelink/blox'
import { defineSchema, $, Alert } from '@bagelink/vue'
const schema = defineSchema({
	useDatastoreData: $.checkbox('Use Torah Books datastore content', { default: true }),
	datastoreExplanation: $.display(Alert, {
		info: true,
		thin: true,
		title: 'This is a datastore-based block',
		message:
			'Books, Torah portions, and articles are assembled automatically from the datastore. Disable “Use Torah Books datastore content” to enter them manually.',
	}).if('useDatastoreData eq true'),
	books: $.array(
		'Books',
		defineSchema({
			name: $.text('Book Name'),
			articles: $.array(
				'Articles',
				defineSchema({
					title: $.text('Title'),
					authors: $.array(
						'Authors',
						defineSchema({
							name: $.text('Name'),
							title: $.text('Title'),
							image: $.upload('Image'),
						}),
						{ collapsible: true },
					),
					image: $.upload('Image'),
					category: $.text('Category'),
					link: $.text('Link'),
				}),
				{ collapsible: true },
			),
			portions: $.array(
				'Torah Portions',
				defineSchema({
					name: $.text('Name'),
					verses: $.text('Verses'),
				}),
				{ collapsible: true },
			),
		}),
		{ collapsible: true },
	).if('useDatastoreData eq false'),
})
export const BlockBooks = defineBlock({
	thumbnail: '/block-thumbs/BlockBooks.svg',
	category: 'Listings',
	order: 180,
	name: 'BlockBooks',
	label: 'Torah Books',
	icon: 'menu_book',
	schema,
	defaults: {
		useDatastoreData: true,
		books: [],
	},
	dataSource: [
		{ collection: 'books', store: 'the_torah', label: 'Books' },
		{ collection: 'parashahs', store: 'the_torah', label: 'Torah Portions' },
		{ collection: 'articles', store: 'the_torah', label: 'Articles' },
	],
})
</script>
<script setup lang="ts">
import { useBloxRuntime } from '@bagelink/blox'
import { Btn, Icon, Image, Skeleton, Swiper } from '@bagelink/vue'
import { ref, computed } from 'vue'
import AuthorSmallLink from '@/components/blocks/AuthorSmallLink.vue'
import { useWeeklyParasha } from '@/composables/useWeeklyParasha'
import { useBooksWithArticles } from '@/composables/useBooksWithArticles'
import { useParashahSlugs } from '@/composables/useParashahSlugs'
import { articleTitleText, sanitizeArticleTitleHtml } from '@/utils/articleHtml'



interface Author {
	name: string
	title: string
	image: string
	slug?: string
	link?: string
}

interface Article {
	title: string
	authors: Author[]
	image: string
	/** Label above the title — the parashah name for datastore books. */
	parasha?: string
	category?: string
	link: string
}

interface Book {
	name: string
	articles: Article[]
	portions: { name: string; verses: string }[]
}

const props = defineProps<{
	useDatastoreData?: boolean
	books?: Book[]
}>()

// Live articles from the datastore (mapped book → its parashot's articles).
const PREVIEW_BOOKS: Book[] = [
	{
		name: 'Genesis',
		articles: [
			{
				title: 'Reading Genesis in Its Ancient Context',
				authors: [{ name: 'Sample Author', title: 'Biblical Scholar', image: '/webclip.png' }],
				image: '/og.jpg',
				parasha: 'Bereshit',
				category: 'Bereshit',
				link: '/',
			},
		],
		portions: [
			{ name: 'Bereshit', verses: 'Genesis 1:1–6:8' },
			{ name: 'Noach', verses: 'Genesis 6:9–11:32' },
		],
	},
]

const { isPreview } = useBloxRuntime()
const { books: liveBooks, loading } = useBooksWithArticles()
const books = computed<Book[]>(() => {
	if (props.useDatastoreData === false) return props.books ?? []
	if (liveBooks.value.length || loading.value || !isPreview.value) return liveBooks.value
	return PREVIEW_BOOKS
})

const showArticleSkeleton = computed(
	() => props.useDatastoreData !== false && loading.value && !activeBookData.value?.articles.length,
)

const bookNames = computed(() => books.value.map(b => b.name))

// Default the open book to the one containing this week's Torah portion, so the
// list reflects "what's read this week". Falls back to the first book (e.g. on a
// festival Shabbat with no weekly sidra, or if the book isn't in the data).
const { primary: weeklyPortion } = useWeeklyParasha()
const { pathFor: parashahLink } = useParashahSlugs()
const weeklyBook = computed(() => weeklyPortion.value?.book ?? '')

const userPickedBook = ref('')
const activeBook = computed(
	() =>
		userPickedBook.value ||
		(weeklyBook.value && bookNames.value.includes(weeklyBook.value) ? weeklyBook.value : '') ||
		bookNames.value[0] ||
		'',
)

function setActiveBook(book: string) {
	userPickedBook.value = book
}

const activeBookData = computed(() => books.value.find(b => b.name === activeBook.value))

// The seeded `verses` may be prefixed with the book name (e.g. "Deuteronomy
// 1:1-3:22"). Strip a leading known book name so the sidebar shows just the
// chapter reference ("1:1-3:22") — works on existing CMS content without a
// re-seed.
const bookNameSet = computed(() => new Set(bookNames.value))
function versesOnly(verses: string): string {
	const firstWord = verses.split(' ')[0]
	return bookNameSet.value.has(firstWord) ? verses.slice(firstWord.length).trimStart() : verses
}

function getArticleSlides(): Article[][] {
	// Three pieces per slide (one per parashah), so Prev / Next walks the whole
	// book instead of stopping after a single extra page.
	const articles = activeBookData.value?.articles ?? []
	const slides: Article[][] = []
	for (let i = 0; i < articles.length; i += 3) {
		slides.push(articles.slice(i, i + 3))
	}
	return slides
}

// Stable identity for the Swiper `items` prop — a new array on every render
// makes the loop rebuild its duplicated slides.
const articleSlides = computed(() => getArticleSlides())
const canLoop = computed(() => articleSlides.value.length > 1)
</script>

<template>
	<div v-if="books.length || showArticleSkeleton" class="bg-gray">
		<section class="w1100px m_m-0 m_w-100p p-3 m_px-1 m_py-1-75 testMe1">
			<div
				class="flex space-evenly pb-3 m_pb-1-75 m_flex-wrap m_justify-content-center m_gap-row-1"
			>
				<Btn
					v-for="book in bookNames"
					:key="book"
					:class="{ activeBook: activeBook === book }"
					:value="book"
					:ripple="false"
					flat
					class="txt22 m_txt19 opacity-6 m_px-1 bookBtn"
					@click="setActiveBook(book)"
				/>
			</div>

			<div class="grid grid-wrap-4 gap-4 m_gap-1">
				<div
					v-if="showArticleSkeleton"
					class="grid-span-3 m_grid-span-1 w-100p"
					role="status"
					aria-label="Loading articles"
					aria-busy="true"
				>
					<div class="grid gap-2" aria-hidden="true">
						<div
							v-for="index in 3"
							:key="index"
							class="grid grid-wrap-2 gap-1-5 align-items-center m_grid-wrap-7 m_gap-1 m_align-items-start"
						>
							<Skeleton class="w-100p aspect-ratio-16-9 m_grid-span-3" />
							<div class="grid gap-05 m_grid-span-4">
								<Skeleton width="30%" height="0.875rem" />
								<Skeleton width="100%" height="1.25rem" />
								<Skeleton width="75%" height="1.25rem" />
								<Skeleton width="50%" height="0.875rem" />
							</div>
						</div>
					</div>
				</div>
				<Swiper
					v-else-if="activeBookData?.articles.length"
					class="grid-span-3 w-100p"
					:items="articleSlides"
					:slides-per-view="1"
					:navigation="true"
					:loop="canLoop"
				>
					<template #default="{ item }">
						<div class="w-100p">
							<div class="flex flex-column gap-2">
								<div class="w-100p" v-for="article in item as Article[]" :key="article.title">
									<div
										class="decoration-none grid grid-wrap-2 gap-1-5 align-items-center m_grid-wrap-7 m_gap-1 m_align-items-start"
									>
										<RouterLink :to="article.link" class="decoration-none m_grid-span-3">
											<Image
												:src="article.image"
												:alt="articleTitleText(article.title)"
												ratio="16-9"
												class="w-100p cover aspect-ratio-16-9 h200px m_h-auto"
											/>
										</RouterLink>
										<div class="m_grid-span-4">
											<p class="color-primary m_txt13">
												{{ article.parasha || article.category }}
											</p>

											<RouterLink :to="article.link" class="decoration-none">
								<h4
									class="my-0 regular txt20 m_txt15 line-height-14 m_line-height-16 balance pb-05 m_pb-025"
									v-html="sanitizeArticleTitleHtml(article.title)"
								/>
											</RouterLink>
											<div class="m_txt13">
								<AuthorSmallLink
									v-for="author in article.authors"
									:key="author.name"
									:author="author"
									:show-image="false"
								/>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</template>
					<template #navigation="{ prev, next }">
						<div v-if="canLoop" class="grid grid-wrap-2 gap-1-5 pt-1">
							<div class="flex justify-content-center">
								<Btn flat color="primary" class="px-1" @click="prev()">
									<Icon name="chevron_left" :weight="200" :size="2" />
									<p>Prev</p>
								</Btn>
								<Btn flat color="primary" class="px-1" @click="next()">
									<p>Next</p>
									<Icon name="chevron_right" :weight="200" :size="2" />
								</Btn>
							</div>
						</div>
					</template>
				</Swiper>

				<div class="m_grid-span-3 flex column align-items-start m_inline">
					<component
						:is="parashahLink(portion.name) ? 'router-link' : 'span'"
						v-for="portion in activeBookData?.portions ?? []"
						:key="portion.name"
						:to="parashahLink(portion.name) || undefined"
						class="color-gray-70 decoration-none mb-1 m_my-05"
					>
						<span class="pe-05 m_pe-1">{{ portion.name }}</span>
						<span class="txt11 m_none">{{ versesOnly(portion.verses) }}</span>
					</component>
				</div>
			</div>
		</section>
	</div>
</template>

<style>
.activeBook {
	color: var(--bgl-primary) !important;
	opacity: 1 !important;
	background-color: none !important;
}
.activeBook::after,
.bookBtn:hover::after {
	content: '';
	display: block;
	width: 40px !important;
	height: 2px !important;
	background: var(--bgl-primary);
	margin-top: 5px !important;
	margin-inline: auto;
}
@media (max-width: 910px) {
	.swi-wrap.has-navigation {
		padding: 10px !important;
	}
	.activeBook::after,
	.bookBtn:hover::after {
		margin-top: 20px !important;
	}
}
</style>
